import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzCheckboxWebComponent } from '../../../ui/gzCheckbox';
import { LicenseConstants } from '../../LicenseConstants';
import { getLocalizedAsset } from '../../../utils/i18n.utils';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { CompanyData } from '../../../company/types/company-data';
import _ from 'lodash';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { htmlEncode } from '../../../utils/escape.utils';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';

interface TrialCheckboxElements {
  $checkbox: GzCheckboxWebComponent;
  $moduleInfoText: HTMLElement;
  $infoText: HTMLElement;
  $link: GzLinkWebComponent;
}

interface TrialCheckboxElementsAddons {
  $checkbox: GzCheckboxWebComponent;
  $infoText: HTMLElement;
  $link: GzLinkWebComponent;
}

export class LicenseTrialSubFormView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $productDetailsTitle: GzTitleWebComponent;
    $productDetailsSubtitle: HTMLElement;

    elite: TrialCheckboxElements;
    officeSecurityPack: TrialCheckboxElements;
    atd: TrialCheckboxElements;
    fullDiskEncryption: TrialCheckboxElementsAddons;
    patchManagement: TrialCheckboxElementsAddons;
    securityForStorage: TrialCheckboxElementsAddons;
    emailSecurity: TrialCheckboxElementsAddons;
    mobileSecurity: TrialCheckboxElementsAddons;
  };

  public formGroup: FormGroup = new FormGroup({});
  private company: CompanyData;
  private trialProductId: number;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#wrapper'),
      $productDetailsTitle: this.shadow.querySelector('#product-details-title'),
      $productDetailsSubtitle: this.shadow.querySelector('#product-details-subtitle'),

      elite: {
        $checkbox: this.shadow.querySelector('#elite-checkbox'),
        $moduleInfoText: this.shadow.querySelector('#elite-module-info-text'),
        $infoText: this.shadow.querySelector('#elite-info-text'),
        $link: this.shadow.querySelector('#elite-link'),
      },

      officeSecurityPack: {
        $checkbox: this.shadow.querySelector('#office-security-pack-checkbox'),
        $moduleInfoText: this.shadow.querySelector('#office-security-pack-module-info-text'),
        $infoText: this.shadow.querySelector('#office-security-pack-info-text'),
        $link: this.shadow.querySelector('#office-security-pack-link'),
      },

      atd: {
        $checkbox: this.shadow.querySelector('#atd-checkbox'),
        $moduleInfoText: this.shadow.querySelector('#atd-module-info-text'),
        $infoText: this.shadow.querySelector('#atd-info-text'),
        $link: this.shadow.querySelector('#atd-link'),
      },

      fullDiskEncryption: {
        $checkbox: this.shadow.querySelector('#full-disk-encryption-checkbox'),
        $infoText: this.shadow.querySelector('#full-disk-encryption-info-text'),
        $link: this.shadow.querySelector('#full-disk-encryption-link'),
      },

      patchManagement: {
        $checkbox: this.shadow.querySelector('#patch-management-checkbox'),
        $infoText: this.shadow.querySelector('#patch-management-info-text'),
        $link: this.shadow.querySelector('#patch-management-link'),
      },

      securityForStorage: {
        $checkbox: this.shadow.querySelector('#security-for-storage-checkbox'),
        $infoText: this.shadow.querySelector('#security-for-storage-info-text'),
        $link: this.shadow.querySelector('#security-for-storage-link'),
      },

      emailSecurity: {
        $checkbox: this.shadow.querySelector('#email-security-checkbox'),
        $infoText: this.shadow.querySelector('#email-security-info-text'),
        $link: this.shadow.querySelector('#email-security-link'),
      },

      mobileSecurity: {
        $checkbox: this.shadow.querySelector('#mobile-security-checkbox'),
        $infoText: this.shadow.querySelector('#mobile-security-info-text'),
        $link: this.shadow.querySelector('#mobile-security-link'),
      },
    };

    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    const learnMoreTranslation = `<span>${htmlEncode(this.dependencies.translateFn('Learn more'))}</span>`;
    this.dom.$productDetailsTitle.innerText = this.dependencies.translateFn('Product details');
    this.dom.$productDetailsSubtitle.innerText = this.dependencies.translateFn(
      'The trial license grants the new company access to the following Bitdefender products and add-ons for the next 30 days:'
    );

    // elite checkbox
    this.dom.elite.$checkbox.label = this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdNextGen);
    this.dom.elite.$infoText.innerText = this.dependencies.translateFn(
      '{productName} is a single-agent, single-console solution for physical, virtual, mobile, cloud-based endpoints, and email. It provides integrated endpoint protection, risk management, and attack forensics, through more than 30 machine learning-driven security technologies.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdNextGen),
      }
    );
    this.dom.elite.$link.innerHTML = learnMoreTranslation;
    this.dom.elite.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.Elite,
      this.dependencies.getUserLanguage()
    );

    // office security pack checkbox
    this.dom.officeSecurityPack.$checkbox.label = this.stringsTranslator.translateProductId(
      LicenseProductIdEnum.ProductIdOfficeSecurityPack
    );
    this.dom.officeSecurityPack.$infoText.innerText = this.dependencies.translateFn(
      '{productName} is a single-agent, single-console solution for physical, virtual, mobile, cloud-based endpoints, and email. It provides integrated endpoint protection, risk management, and attack forensics, through more than 30 machine learning-driven security technologies.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdOfficeSecurityPack),
      }
    );
    this.dom.officeSecurityPack.$link.innerHTML = learnMoreTranslation;
    this.dom.officeSecurityPack.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.OfficeSecurityPack,
      this.dependencies.getUserLanguage()
    );

    // atd pack checkbox
    this.dom.atd.$checkbox.label = this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdAtd);
    this.dom.atd.$infoText.innerText = this.dependencies.translateFn(
      '{productName} is a single-agent, single-console solution for physical, virtual, mobile, cloud-based endpoints, and email. It provides integrated endpoint protection, risk management, and attack forensics, through more than 30 machine learning-driven security technologies.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdAtd),
      }
    );
    this.dom.atd.$link.innerHTML = learnMoreTranslation;
    this.dom.atd.$link.href = getLocalizedAsset(LicenseConstants.InfoLinks.Atd, this.dependencies.getUserLanguage());

    // full disk encryption
    this.dom.fullDiskEncryption.$checkbox.label = this.dependencies.translateFn('{productName} (add-on)', {
      productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdVolumeEncryption),
    });
    this.dom.fullDiskEncryption.$infoText.innerText = this.dependencies.translateFn(
      '{productName} encrypts boot and non-boot volumes, on fixed disks, on desktop computers and laptops, and provides remote management of the encryption keys.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdVolumeEncryption),
      }
    );
    this.dom.fullDiskEncryption.$link.innerHTML = learnMoreTranslation;
    this.dom.fullDiskEncryption.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.FullDiskEncryption,
      this.dependencies.getUserLanguage()
    );

    // patch management
    this.dom.patchManagement.$checkbox.label = this.dependencies.translateFn('{productName} (add-on)', {
      productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdPatchManagement),
    });
    this.dom.patchManagement.$infoText.innerText = this.dependencies.translateFn(
      '{productName} enables organizations to keep operating systems and software applications up to date.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdPatchManagement),
      }
    );
    this.dom.patchManagement.$link.innerHTML = learnMoreTranslation;
    this.dom.patchManagement.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.PatchManagement,
      this.dependencies.getUserLanguage()
    );

    // security for storage
    this.dom.securityForStorage.$checkbox.label = this.dependencies.translateFn('{productName} (add-on)', {
      productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdStorageProtection),
    });
    this.dom.securityForStorage.$infoText.innerText = this.dependencies.translateFn(
      '{productName} delivers next-generation protection for ICAP-compatible file-sharing and network-storage systems.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdStorageProtection),
      }
    );
    this.dom.securityForStorage.$link.innerHTML = learnMoreTranslation;
    this.dom.securityForStorage.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.SecurityForStorage,
      this.dependencies.getUserLanguage()
    );
    // email security
    this.dom.emailSecurity.$checkbox.label = this.dependencies.translateFn('{productName} (add-on)', {
      productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdEmailSecurity),
    });
    this.dom.emailSecurity.$infoText.innerText = this.dependencies.translateFn(
      '{productName} is a complete business email protection that goes beyond malware and other traditional threats, and stops modern, targeted and sophisticated email threats, including Business Email Compromise (BEC) and CEO fraud.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdEmailSecurity),
      }
    );
    this.dom.emailSecurity.$link.innerHTML = learnMoreTranslation;
    this.dom.emailSecurity.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.EmailSecurity,
      this.dependencies.getUserLanguage()
    );
    // mobile security
    this.dom.mobileSecurity.$checkbox.label = this.dependencies.translateFn('{productName} (add-on)', {
      productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdMobileSecurity),
    });
    this.dom.mobileSecurity.$infoText.innerText = this.dependencies.translateFn(
      '{productName} is a mobile security solution that supports Android, iOS, and Chromebooks, and enables your company to achieve compliance by protecting and managing any mobile devices from a centralized platform.',
      {
        productName: this.stringsTranslator.translateProductId(LicenseProductIdEnum.ProductIdMobileSecurity),
      }
    );
    this.dom.mobileSecurity.$link.innerHTML = learnMoreTranslation;
    this.dom.mobileSecurity.$link.href = getLocalizedAsset(
      LicenseConstants.InfoLinks.MobileSecurity,
      this.dependencies.getUserLanguage()
    );
  }

  buildSuccessView(data: any) {
    // set default value for trial product id for create scenario
    this.trialProductId = LicenseProductIdEnum.ProductIdNextGen;
    this.setVisibleTrialProductCheckbox();
  }

  buildErrorView(error: Error) {}

  public setCompanyData(company: CompanyData) {
    this.company = _.cloneDeep(company);
    this.obtainTrialProductId();
    this.setVisibleTrialProductCheckbox();
  }

  private obtainTrialProductId() {
    const firstBaseLicense = CompanyData.getFirstBaseLicense(this.company);
    this.trialProductId = firstBaseLicense?.productId || LicenseProductIdEnum.ProductIdNextGen;
  }

  private setVisibleTrialProductCheckbox() {
    toggleVisibility(this.dom.elite.$checkbox, this.trialProductId === LicenseProductIdEnum.ProductIdNextGen);
    toggleVisibility(this.dom.elite.$moduleInfoText, this.trialProductId === LicenseProductIdEnum.ProductIdNextGen);

    toggleVisibility(
      this.dom.officeSecurityPack.$checkbox,
      this.trialProductId === LicenseProductIdEnum.ProductIdOfficeSecurityPack
    );
    toggleVisibility(
      this.dom.officeSecurityPack.$moduleInfoText,
      this.trialProductId === LicenseProductIdEnum.ProductIdOfficeSecurityPack
    );

    toggleVisibility(this.dom.atd.$checkbox, this.trialProductId === LicenseProductIdEnum.ProductIdAtd);
    toggleVisibility(this.dom.atd.$moduleInfoText, this.trialProductId === LicenseProductIdEnum.ProductIdAtd);
  }
}
